
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionStatus } from "@/hooks/account/useSubscriptionStatus";
import { storeStrategyForRedirect, storeRedirectUrl } from "@/utils/strategySessionUtils";

interface StrategyDescriptionProps {
  name: string;
  onFollowClick?: () => void;
  isPro?: boolean;
  strategy?: any; // Strategy data object
  originalStrategyId?: string;
}

export const StrategyDescription = ({ 
  name, 
  onFollowClick, 
  isPro = false, 
  strategy,
  originalStrategyId
}: StrategyDescriptionProps) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  // Get subscription status using the refactored hook
  const { subscriptionStatus } = useSubscriptionStatus(userId);
  
  // Check if the user has an active subscription
  const hasActiveSubscription = subscriptionStatus.status === "active";

  // Check if user is authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    };
    
    checkAuthStatus();
  }, []);

  // Determine if we're on the Phemex landing page
  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);

  // Get user ID on component mount
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
        }
      } catch (err) {
        console.error("Error getting user:", err);
      }
    };
    
    getUser();
  }, []);

  const handleButtonClick = () => {
    // If on Phemex landing and not authenticated, redirect to signup
    if (isPhemexLanding && !isAuthenticated) {
      // Store strategy data for after authentication
      if (strategy) {
        storeStrategyForRedirect({
          ...strategy,
          originalStrategyId
        });
        
        // Determine where to redirect after signup
        const redirectPath = isPro 
          ? (hasActiveSubscription ? '/follow-strategy-premium-existing' : '/follow-strategy')
          : '/follow-strategy-free';
          
        storeRedirectUrl(redirectPath);
      }
      
      // Navigate to signup
      navigate('/signup', { state: { fromPhemex: true } });
      return;
    }
    
    // If there's a custom onFollowClick handler, use it and return
    if (onFollowClick) {
      onFollowClick();
      return;
    }
    
    // Handle direct navigation based on strategy type and subscription status
    if (!strategy) {
      console.error("Strategy data not provided to StrategyDescription");
      return;
    }
    
    // Direct to the correct page based on subscription status
    if (isPro && hasActiveSubscription) {
      navigate('/follow-strategy-premium-existing', {
        state: {
          strategy: strategy,
          originalStrategyId: originalStrategyId
        }
      });
    } else if (isPro) {
      navigate('/follow-strategy', {
        state: {
          strategy: strategy,
          originalStrategyId: originalStrategyId
        }
      });
    } else {
      navigate('/follow-strategy-free', {
        state: {
          strategy: strategy,
          originalStrategyId: originalStrategyId
        }
      });
    }
  };

  return (
    <div className="p-6 bg-white">
      <h3 className="font-medium text-lg text-gray-800 mb-4">About {name}</h3>
      
      <p className="text-gray-600 mb-6">
        { strategy.description }
      </p>
      
      <Button 
        onClick={handleButtonClick}
        className={`w-full ${
          isPro 
            ? "bg-[#F6C528] text-[#012846] hover:bg-[#F7D44C]" 
            : "bg-[#012846] text-white hover:bg-[#407096]"
        } transition-all duration-300 py-6 text-base font-medium`}
      >
        Start Following This Strategy
        <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
};
